import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Row, Col, Table, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLabsStock } from "../../context/LabsStockContext";
import * as actions from "../../redux/actions";
import { getExpiryColor } from "../../utils";
import moment from "moment";
import "../index.less";

// ExpandedRow passes the selected register ID and warehouse-scoped visible IDs.
// Live warehouse stock is not used as a historical snapshot.
const OmniCurrentStockTable = ({ registerId, registerIds = [], warehouseId: rowWarehouseId }) => {
  const dispatch = useDispatch();
  const [sortedInfo, setSortedInfo] = useState({});
  const { wareHouseId: contextWareHouseId } = useLabsStock();

  const snapshots = useSelector(state => state.labsStockManagementReducer.omniSnapshotsByRegisterId || {});
  const legacyByWarehouse = useSelector(state => state.labsStockManagementReducer.omniLegacyStockByWarehouse || {});
  const loading = useSelector(state => state.labsStockManagementReducer.omniCurrentStockLoading || false);
  const pendingByRegisterId = useSelector(state => state.labsStockManagementReducer.omniSnapshotsPendingByRegisterId || {});
  const error = useSelector(state => state.labsStockManagementReducer.omniCurrentStockError || null);

  // Prefer the parent row's warehouse over shared context (which can change
  // while another register row is open).
  const warehouseId = Number(rowWarehouseId ?? contextWareHouseId);
  const currentRegisterId = Number(registerId);
  const validRegister = Number.isSafeInteger(currentRegisterId) && currentRegisterId > 0;
  const validWarehouse = Number.isSafeInteger(warehouseId) && warehouseId > 0;

  // Building the request from the visible set preserves the prior single-call
  // behavior. Register IDs must belong to the same warehouse.
  const allRegisterIds = [registerId, ...(Array.isArray(registerIds) ? registerIds : [])];
  const distinctIds = [...new Set(
    allRegisterIds.map(Number).filter(id => Number.isSafeInteger(id) && id > 0)
  )];
  // Always include the selected register, even when there are >50 visible IDs.
  const requestedIds = validRegister
    ? [currentRegisterId, ...distinctIds.filter(id => id !== currentRegisterId).sort((a, b) => a - b)].slice(0, 50)
    : distinctIds.sort((a, b) => a - b).slice(0, 50);

  const missingIds = requestedIds.filter(id =>
    !Object.prototype.hasOwnProperty.call(snapshots, String(id)) &&
    !Object.prototype.hasOwnProperty.call(pendingByRegisterId, String(id))
  );
  const requestKey = `${warehouseId}:${missingIds.join(",")}`;
  const requestedKeys = useRef(new Set());

  useEffect(() => {
    if (!validWarehouse || !validRegister || missingIds.length === 0) return;
    if (requestedKeys.current.has(requestKey)) return;
    requestedKeys.current.add(requestKey);
    dispatch(actions.fetchOmniCurrentStock({ warehouseId, registerIds: missingIds }));
  }, [dispatch, validWarehouse, validRegister, warehouseId, requestKey]);

  // Reload only missing snapshots if the request fails. This is a deliberate
  // user action, avoiding an automatic retry loop from the loading/error state.
  const retry = () => {
    if (!validWarehouse || missingIds.length === 0) return;
    requestedKeys.current.delete(requestKey);
    requestedKeys.current.add(requestKey);
    dispatch(actions.fetchOmniCurrentStock({ warehouseId, registerIds: missingIds }));
  };

  const entry = validRegister ? snapshots[String(currentRegisterId)] : null;
  const isSnapshot = entry?.isSnapshot === true;
  const isLegacy = entry?.isSnapshot === false;
  const stock = isSnapshot
    ? (entry?.stock || [])
    : (isLegacy ? (legacyByWarehouse[String(warehouseId)] || []) : []);

  const title = isSnapshot
    ? `OMNI Stock Snapshot - ${moment(entry.snapshotCapturedAt).format("DD MMM YYYY HH:mm")}`
    : isLegacy
      ? "Current OMNI Stock (Legacy register — no historical snapshot)"
      : "OMNI Stock Snapshot";

  const columns = useMemo(() => {
    const textSort = (key) => (a, b) => String(a[key] ?? "").localeCompare(String(b[key] ?? ""));
    const col = (title, key, width, sorter) => ({
      title: <div className="stock-table-header-text">{title}</div>,
      dataIndex: key,
      key,
      width,
      sorter,
      sortOrder: sortedInfo.columnKey === key ? sortedInfo.order : null,
    });
    return [
      col("Stock Code", "stockCode", "16%", textSort("stockCode")),
      col("Stock Description", "stockDescription", "30%", textSort("stockDescription")),
      col("Lot No", "lotNo", "20%", textSort("lotNo")),
      {
        ...col("Expiry Date", "expiryDate", "20%", (a, b) => String(a.expiryDate ?? "").localeCompare(String(b.expiryDate ?? ""))),
        render: (value) => {
          if (!value) return "";
          const formatted = /^\d{4}\/\d{2}\/\d{2}$/.test(value) ? value : moment(value).format("YYYY/MM/DD");
          const color = getExpiryColor(value);
          return color ? <span className={`expiry-highlight expiry-highlight-${color}`}>{formatted}</span> : formatted;
        },
      },
      col("Level", "level", "12%", (a, b) => Number(a.level || 0) - Number(b.level || 0)),
    ];
  }, [sortedInfo]);

  const tableData = useMemo(() => stock.map((item, index) => ({
    key: `${item.stockcode}|${item.lotno}|${item.expirydate}|${index}`,
    stockCode: item.stockcode,
    stockDescription: item.stockdescription,
    lotNo: item.lotno,
    expiryDate: item.expirydate,
    level: item.level,
  })), [stock]);

  const invalidContext = !validWarehouse || !validRegister;
  const waitingForData = !invalidContext && !entry;

  return (
    <Row className="stock-doc-control-parent-row">
      <Col span={24} className="stock-table-heading-col">
        <Row align="middle" gutter={[50, 0]} justify="space-between">
          <Col><span className="heading-span">{title}</span></Col>
        </Row>
      </Col>
      {invalidContext && (
        <Col span={24}>
          <Alert type="warning" showIcon message="The selected register ID and warehouse ID are required to display its snapshot." />
        </Col>
      )}
      {isLegacy && (
        <Col span={24}>
          <Alert type="warning" showIcon message="This register was created before snapshot capture. Current OMNI data is shown for reference, not as the original stock baseline." />
        </Col>
      )}
      {!invalidContext && error && waitingForData && (
        <Col span={24}>
          <Alert type="error" showIcon message={typeof error === "string" ? error : "Unable to load OMNI stock snapshots"}
            action={<Button size="small" onClick={retry}>Retry</Button>} />
        </Col>
      )}
      <Col span={24} style={{ minWidth: 0 }}>
        {/* Prevent overflow beneath the existing blue-bordered OMNI panel.
            Keep the original Ant Design Table and all its styling untouched. */}
        <div
          style={{
            maxHeight: 390,
            width: "100%",
            minWidth: 0,
            overflowY: "auto",
            overflowX: "auto",
            overscrollBehavior: "contain",
          }}
        >
          <Table
            scroll={{ x: 485, y: 350 }}
            tableLayout="fixed"
            className="special-table stock-table omni-stock-table ims-doc-table reduced-padding-table"
            columns={columns}
            dataSource={tableData}
            pagination={false}
            onChange={(pagination, filters, sorter) => setSortedInfo(sorter || {})}
            loading={waitingForData && (loading || !!pendingByRegisterId[String(currentRegisterId)])}
          />
        </div>
      </Col>
    </Row>
  );
};

export default OmniCurrentStockTable;

